import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { jwtDecode } from 'jwt-decode';
import {
  PUNDIT_PUROHIT_AUTH_SOURCE_KEY,
  PUNDIT_PUROHIT_TOKEN_KEY,
  setStoredPunditPurohitUser,
} from '../utils/auth';

const initialForm = {
  firstName: '',
  lastName: '',
  mobile: '',
  email: '',
  password: '',
  registerAsPundit: false,
};

const PoojaLogin = ({ initialMode = 'signin', embedded = false, onSuccess, onClose }) => {
  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState(initialForm);
  const [signInAsUdyogUser, setSignInAsUdyogUser] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const isSignUp = mode === 'signup';
  const apiBase = process.env.REACT_APP_API_BASE_URL;

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const persistUserSession = (token, user, source) => {
    const decoded = token ? jwtDecode(token) : {};
    const sessionUser = user || {
      id: decoded.id,
      name: decoded.userfirstname || decoded.email?.split('@')[0] || 'User',
      email: decoded.email,
      role: decoded.isPundit ? 'pundit' : 'devotee',
      source,
      punditApprovalStatus: decoded.punditApprovalStatus || 'not_requested',
      isPundit: Boolean(decoded.isPundit),
    };

    setStoredPunditPurohitUser(sessionUser);
    localStorage.setItem(PUNDIT_PUROHIT_AUTH_SOURCE_KEY, source);

    if (source === 'udyog_connect') {
      localStorage.setItem('token', token);
    } else {
      localStorage.setItem(PUNDIT_PUROHIT_TOKEN_KEY, token);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      const url = signInAsUdyogUser
        ? `${apiBase}/api/login`
        : `${apiBase}/api/punditpurohit/auth/login`;

      const response = await axios.post(url, {
        email: form.email,
        password: form.password,
      });

      persistUserSession(
        response.data.token,
        response.data.user,
        signInAsUdyogUser ? 'udyog_connect' : 'pundit_purohit'
      );

      if (embedded) {
        onSuccess?.();
      } else {
        navigate('/uc/panditpurshothit');
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Invalid user ID or password.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      const response = await axios.post(`${apiBase}/api/punditpurohit/auth/signup`, {
        firstName: form.firstName,
        lastName: form.lastName,
        mobile: form.mobile,
        email: form.email,
        password: form.password,
        registerAsPundit: form.registerAsPundit,
      });

      setMessage(response.data.message || 'Signup complete. You can sign in now.');
      setMode('signin');
      setForm((current) => ({ ...initialForm, email: current.email }));
    } catch (err) {
      setError(err?.response?.data?.message || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setMode(isSignUp ? 'signin' : 'signup');
    setError('');
    setMessage('');
  };

  return (
    <div className={embedded ? 'pooja-login-wrapper embedded' : 'pooja-login-wrapper'}>
      {!embedded && <div className="login-bg-overlay"></div>}

      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.35 }}
        className="pooja-login-card"
      >
        {embedded && (
          <button type="button" className="auth-modal-close" onClick={onClose}>
            Close
          </button>
        )}
        <div className="login-header">
          <div className="logo-brand">UDYOG<span>Connect</span></div>
          <h2>{isSignUp ? 'Create Account' : 'Divine Rituals'}</h2>
          <p>{isSignUp ? 'Create your Pundit Purohit account' : 'Sign in to manage your bookings and rituals'}</p>
        </div>

        <div className="auth-mode-toggle">
          <button type="button" className={!isSignUp ? 'active' : ''} onClick={() => setMode('signin')}>
            Sign In
          </button>
          <button type="button" className={isSignUp ? 'active' : ''} onClick={() => setMode('signup')}>
            Sign Up
          </button>
        </div>

        <form onSubmit={isSignUp ? handleSignUp : handleLogin}>
          {isSignUp && (
            <>
              <div className="form-row">
                <div className="input-group">
                  <label>First Name</label>
                  <input
                    type="text"
                    required
                    value={form.firstName}
                    onChange={(e) => updateForm('firstName', e.target.value)}
                  />
                </div>
                <div className="input-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    required
                    value={form.lastName}
                    onChange={(e) => updateForm('lastName', e.target.value)}
                  />
                </div>
              </div>

              <div className="input-group">
                <label>Mobile Number</label>
                <input
                  type="tel"
                  required
                  inputMode="tel"
                  pattern="[+]?[0-9() .-]{10,20}"
                  title="Enter a valid phone number with at least 10 digits"
                  value={form.mobile}
                  onChange={(e) => updateForm('mobile', e.target.value)}
                />
              </div>
            </>
          )}

          <div className="input-group">
            <label>{signInAsUdyogUser && !isSignUp ? 'User ID / Email' : 'Email Address'}</label>
            <input
              type="email"
              required
              pattern="[^\\s@]+@[^\\s@]+\\.[^\\s@]{2,}"
              title="Enter a valid email address"
              placeholder="name@example.com"
              value={form.email}
              onChange={(e) => updateForm('email', e.target.value)}
            />
          </div>

          <div className="input-group">
            <label>Password</label>
            <input
              type="password"
              required
              minLength={isSignUp ? 6 : undefined}
              placeholder="Enter password"
              value={form.password}
              onChange={(e) => updateForm('password', e.target.value)}
            />
          </div>

          {!isSignUp && (
            <label className="auth-checkbox">
              <input
                type="checkbox"
                checked={signInAsUdyogUser}
                onChange={(e) => setSignInAsUdyogUser(e.target.checked)}
              />
              Sign in as Udyog Connect user
            </label>
          )}

          {isSignUp && (
            <>
              <label className="auth-checkbox">
                <input
                  type="checkbox"
                  checked={form.registerAsPundit}
                  onChange={(e) => updateForm('registerAsPundit', e.target.checked)}
                />
                Start a basic Pandit application
              </label>
              <p className="auth-provider-profile-link">
                Pandit, spiritual teacher, seva volunteer, or community coordinator?{' '}
                <a href="/uc/panditpurshothit?provider-network=1">Complete the Pandit & Seva Network profile</a> for service, availability, and review details.
              </p>
            </>
          )}

          {error && <div className="auth-alert auth-alert-error">{error}</div>}
          {message && <div className="auth-alert auth-alert-success">{message}</div>}

          <button type="submit" className="login-submit-btn" disabled={loading}>
            {loading ? 'Please wait...' : isSignUp ? 'Create Account' : 'Continue'}
          </button>
        </form>

        <div className="login-footer">
          <p>
            {isSignUp ? 'Already have an account?' : 'New customer?'}{' '}
            <button type="button" className="auth-link-button" onClick={toggleMode}>
              {isSignUp ? 'Sign in' : 'Start here'}
            </button>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default PoojaLogin;
